// CinematicForeground — a very small near-camera layer. These sparse, blurred
// facets are intentionally physical geometry (not a screen overlay): they
// intermittently occlude the lens, giving the planet a nearer and farther.

import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const SHARDS = [
  { position: [-1.58, -0.82, -1.55], rotation: [0.42, 0.22, 0.58], scale: [0.16, 0.72, 0.16], parallax: 0.32 },
  { position: [1.44, 0.7, -2.1], rotation: [-0.36, 0.6, -0.48], scale: [0.11, 0.48, 0.11], parallax: 0.18 },
  { position: [1.22, -0.96, -1.28], rotation: [0.2, -0.58, 0.7], scale: [0.2, 0.58, 0.2], parallax: 0.42 },
] as const;

const VISIBLE_CHAPTERS = new Set([1, 2, 3, 4, 5]);

export function CinematicForeground() {
  const rootRef = useRef<THREE.Group | null>(null);
  const shardRefs = useRef<Array<THREE.Group | null>>([]);
  const materialRefs = useRef<Array<THREE.MeshStandardMaterial | null>>([]);
  const drift = useMemo(() => new THREE.Vector2(), []);

  useFrame(({ camera }, delta) => {
    const root = rootRef.current;
    if (!root) return;

    const reduced = window.__planetReducedMotion === true;
    const mode = window.__planetMode ?? 'narrative';
    const chapter = window.__planetSceneIndex ?? 0;
    const pointer = window.__planetPointer ?? { x: 0, y: 0 };
    const visible = !reduced && mode === 'narrative' && VISIBLE_CHAPTERS.has(chapter);
    root.visible = visible;
    if (!visible) return;

    root.position.copy(camera.position);
    root.quaternion.copy(camera.quaternion);
    const settle = 1 - Math.pow(1 - 0.07, Math.min(delta, 1 / 30) * 60);
    drift.x += (pointer.x - drift.x) * settle;
    drift.y += (pointer.y - drift.y) * settle;
    const speed = Math.abs(window.__planetScrollVelocity ?? 0);
    const targetOpacity = 0.09 + Math.min(0.12, speed * 0.08);

    SHARDS.forEach((shard, index) => {
      const node = shardRefs.current[index];
      if (node) {
        node.position.set(
          shard.position[0] - drift.x * shard.parallax,
          shard.position[1] - drift.y * shard.parallax * 0.7,
          shard.position[2],
        );
      }
      const material = materialRefs.current[index];
      if (material) material.opacity = THREE.MathUtils.lerp(material.opacity, targetOpacity, settle);
    });
  });

  return (
    <group ref={rootRef} name="cinematic-foreground" renderOrder={3}>
      {SHARDS.map((shard, index) => (
        <group
          key={index}
          ref={(node) => {
            shardRefs.current[index] = node;
          }}
          position={shard.position}
          rotation={shard.rotation as [number, number, number]}
          scale={shard.scale}
        >
          <mesh raycast={() => null}>
            <coneGeometry args={[1, 1, 5, 1]} />
            <meshStandardMaterial
              ref={(node) => {
                materialRefs.current[index] = node;
              }}
              color="#111d2a"
              emissive="#0a1620"
              emissiveIntensity={0.12}
              roughness={0.88}
              transparent
              opacity={0}
              depthWrite={false}
              side={THREE.DoubleSide}
              flatShading
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export default CinematicForeground;
